import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle, Clock, KeyRound, Pencil, ShieldOff, Smartphone, XCircle } from 'lucide-react';
import AppLayout from '../components/layout/AppLayout';
import { useTimezone, formatInTimezone, PAGE, deviceStatus } from '@kioskgaming/ui';
import ModalPortal from '../components/ui/ModalPortal';
import PortalEmptyState from '../components/ui/PortalEmptyState';
import {
  approveCashierDevice,
  blockCashierDevice,
  createDevicePairingCode,
  fetchCashierDevices,
  renameCashierDevice,
} from '../services/api';
import { dispatchCashierDevicesChanged } from '../constants';
import type { CashierDevice, CashierDevicePairingCode } from '../types';
import {
  cashierRefLabel,
  DEVICE_NAME_MAX,
  deviceDescription,
  formatCountdown,
  formatWaitingSince,
  isValidDeviceName,
  shopDeviceErrorMessage,
} from '../utils/cashierDevices';

export const DEVICE_LIST_POLL_MS = 10_000;

type ActionResult = { success: boolean; message?: string; code?: string };

function getApiErrorMessage(e: unknown): string | null {
  const anyE = e as { response?: { data?: { message?: string } }; message?: string };
  return anyE?.response?.data?.message || anyE?.message || null;
}

function deviceStatusClass(status: string): string {
  if (status === 'active') return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  if (status === 'pending_approval') return 'border-amber-200 bg-amber-50 text-amber-800';
  if (status === 'temporarily_blocked') return 'border-rose-200 bg-rose-50 text-rose-800';
  return 'border-stone-200 bg-stone-50 text-stone-700';
}

const inputClass =
  'mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500';
const secondaryButtonClass =
  'rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50 disabled:opacity-60';
const primaryButtonClass =
  'rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60';
const dangerButtonClass =
  'rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60';

function ModalShell({ titleId, title, children }: { titleId: string; title: string; children: ReactNode }) {
  return (
    <ModalPortal>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5 shadow-xl"
        >
          <div id={titleId} className="text-lg font-semibold">
            {title}
          </div>
          {children}
        </div>
      </div>
    </ModalPortal>
  );
}

function ShortCodeBadge({ code }: { code?: string }) {
  if (!code) return null;
  return (
    <span className="rounded-md border border-stone-200 bg-stone-50 px-1.5 py-0.5 font-mono text-[11px] font-semibold tracking-wider text-stone-700">
      {code}
    </span>
  );
}

type PairingState = {
  name: string;
  creating: boolean;
  result: CashierDevicePairingCode | null;
};

/** Live mm:ss until the pairing code expires. */
function PairingCountdown({ expiresAt }: { expiresAt: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt]);
  const expired = new Date(expiresAt).getTime() <= now;
  if (expired) {
    return <span className="font-semibold text-rose-700">Expired</span>;
  }
  return (
    <span>
      Expires in{' '}
      <span className="font-mono font-semibold tabular-nums text-stone-900" data-testid="pairing-countdown">
        {formatCountdown(expiresAt, now)}
      </span>
    </span>
  );
}

export default function CashierDevicesPage() {
  const { timezone } = useTimezone();
  const [devices, setDevices] = useState<CashierDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending_approval' | 'active' | 'temporarily_blocked'>('all');

  const [approveTarget, setApproveTarget] = useState<CashierDevice | null>(null);
  const [approveName, setApproveName] = useState('');
  const [rejectTarget, setRejectTarget] = useState<CashierDevice | null>(null);
  const [blockTarget, setBlockTarget] = useState<CashierDevice | null>(null);
  const [renameTarget, setRenameTarget] = useState<CashierDevice | null>(null);
  const [renameName, setRenameName] = useState('');
  const [pairing, setPairing] = useState<PairingState | null>(null);

  /** `silent` = background poll: no spinner, no error toast, keep the current list on failure. */
  const load = useCallback(async (options?: { silent?: boolean }) => {
    const silent = Boolean(options?.silent);
    if (!silent) setLoading(true);
    try {
      const res = await fetchCashierDevices();
      if (!res.success) throw new Error(res.message || 'Failed to load devices');
      setDevices(res.data || []);
    } catch (e: unknown) {
      if (!silent) {
        toast.error(getApiErrorMessage(e) || 'Failed to load devices');
        setDevices([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // A cashier may be waiting at the counter: poll while the page is visible.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void load({ silent: true });
    }, DEVICE_LIST_POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void load({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [load]);

  const waiting = useMemo(() => devices.filter((d) => d.approvalRequest != null), [devices]);

  const metrics = useMemo(() => {
    const pending = devices.filter((d) => d.status === 'pending_approval').length;
    const active = devices.filter((d) => d.status === 'active').length;
    const blocked = devices.filter((d) => d.status === 'temporarily_blocked').length;
    return { total: devices.length, pending, active, blocked };
  }, [devices]);

  const filtered = useMemo(() => {
    if (filter === 'all') return devices;
    return devices.filter((d) => d.status === filter);
  }, [devices, filter]);

  /** Runs a device action; on success notifies the sidebar badge and reloads the list. */
  const runAction = async (
    device: CashierDevice,
    request: () => Promise<ActionResult>,
    successMessage: string,
    fallbackError: string
  ): Promise<boolean> => {
    setActionId(device.id);
    try {
      const res = await request();
      if (!res.success) {
        toast.error(shopDeviceErrorMessage(res, fallbackError));
        return false;
      }
      toast.success(successMessage);
      dispatchCashierDevicesChanged();
      await load({ silent: true });
      return true;
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || fallbackError);
      return false;
    } finally {
      setActionId(null);
    }
  };

  // Modal buttons are disabled while a name is invalid, so the confirm handlers receive a valid target.
  const openApprove = (device: CashierDevice) => {
    setApproveTarget(device);
    setApproveName(device.deviceName.trim().slice(0, DEVICE_NAME_MAX));
  };

  const onConfirmApprove = async (device: CashierDevice) => {
    const unblock = device.status === 'temporarily_blocked';
    const ok = await runAction(
      device,
      () => approveCashierDevice(device.id, { name: approveName.trim() }),
      unblock ? 'Device unblocked' : 'Device approved',
      unblock ? 'Unblock failed' : 'Approve failed'
    );
    if (ok) setApproveTarget(null);
  };

  const onConfirmReject = async (device: CashierDevice) => {
    const ok = await runAction(device, () => blockCashierDevice(device.id), 'Device rejected', 'Reject failed');
    if (ok) setRejectTarget(null);
  };

  const onConfirmBlock = async (device: CashierDevice) => {
    const ok = await runAction(device, () => blockCashierDevice(device.id), 'Device blocked', 'Block failed');
    if (ok) setBlockTarget(null);
  };

  const openRename = (device: CashierDevice) => {
    setRenameTarget(device);
    setRenameName(device.deviceName.trim().slice(0, DEVICE_NAME_MAX));
  };

  const onConfirmRename = async (device: CashierDevice) => {
    const ok = await runAction(
      device,
      () => renameCashierDevice(device.id, renameName.trim()),
      'Device renamed',
      'Rename failed'
    );
    if (ok) setRenameTarget(null);
  };

  const onCreatePairingCode = async (current: PairingState) => {
    const name = current.name.trim();
    // The modal cannot be closed or edited while `creating`, so `current` stays accurate.
    setPairing({ ...current, creating: true });
    try {
      const res = await createDevicePairingCode(name ? { name } : {});
      if (!res.success || !res.data) {
        toast.error(shopDeviceErrorMessage(res, 'Could not create a pairing code'));
        setPairing({ ...current, creating: false });
        return;
      }
      setPairing({ ...current, creating: false, result: res.data });
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Could not create a pairing code');
      setPairing({ ...current, creating: false });
    }
  };

  const closePairing = () => {
    const hadCode = Boolean(pairing?.result);
    setPairing(null);
    // The counter may have used the code while the modal was open.
    if (hadCode) void load({ silent: true });
  };

  const approveNameValid = isValidDeviceName(approveName);
  const renameNameValid = isValidDeviceName(renameName);

  return (
    <AppLayout contentFullWidth>
      <div className="w-full max-w-none">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">{PAGE.devices}</h1>
            <p className="mt-0.5 text-sm text-stone-600">
              Approve and control the counter devices Cashiers use for their shifts.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              onClick={() => setPairing({ name: '', creating: false, result: null })}
            >
              <KeyRound className="h-4 w-4" />
              Add device with pairing code
            </button>
            <button type="button" className={secondaryButtonClass} onClick={() => void load()}>
              Refresh
            </button>
          </div>
        </div>

        {waiting.length > 0 ? (
          <section
            aria-labelledby="waiting-devices-title"
            className="mt-4 rounded-xl border border-amber-200 bg-amber-50"
          >
            <div className="flex items-center justify-between gap-2 border-b border-amber-200 px-4 py-3">
              <div>
                <h2 id="waiting-devices-title" className="text-base font-semibold text-amber-950">
                  Waiting for approval
                </h2>
                <p className="text-xs text-amber-800">
                  A Cashier is at the counter. Check the code on their screen before approving.
                </p>
              </div>
              <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-bold text-white">{waiting.length}</span>
            </div>
            <div className="divide-y divide-amber-200">
              {waiting.map((device) => {
                const request = device.approvalRequest!;
                return (
                  <div
                    key={device.id}
                    data-testid="waiting-device"
                    className="flex flex-wrap items-center justify-between gap-4 px-4 py-4"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-4">
                      <div
                        className="shrink-0 rounded-xl border border-amber-300 bg-white px-3 py-2 text-center font-mono text-3xl font-bold tracking-[0.2em] text-amber-950"
                        aria-label={`Code ${device.shortCode}`}
                      >
                        {device.shortCode}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-stone-900">{device.deviceName}</div>
                        <div className="text-xs text-stone-600">{deviceDescription(device)}</div>
                        <div className="mt-1 text-sm text-stone-800">
                          {cashierRefLabel(request.cashier)} · shift{' '}
                          {formatInTimezone(request.shift.scheduledStartAt, timezone)} –{' '}
                          {formatInTimezone(request.shift.scheduledEndAt, timezone)}
                        </div>
                        <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-amber-800">
                          <Clock className="h-3.5 w-3.5" aria-hidden />
                          Waiting {formatWaitingSince(request.requestedAt)}
                        </div>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        disabled={actionId === device.id}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                        onClick={() => openApprove(device)}
                      >
                        <CheckCircle className="h-4 w-4" />
                        Approve
                      </button>
                      <button
                        type="button"
                        disabled={actionId === device.id}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                        onClick={() => setRejectTarget(device)}
                      >
                        <XCircle className="h-4 w-4" />
                        Reject
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}

        <div className="mt-4 grid gap-3 sm:grid-cols-4">
          <div className="rounded-xl border border-stone-200 bg-white p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-stone-500">Total</div>
            <div className="mt-1 text-2xl font-bold text-stone-900">{metrics.total}</div>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-amber-700">{deviceStatus('pending_approval').label}</div>
            <div className="mt-1 text-2xl font-bold text-amber-900">{metrics.pending}</div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-emerald-700">{deviceStatus('active').label}</div>
            <div className="mt-1 text-2xl font-bold text-emerald-900">{metrics.active}</div>
          </div>
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-rose-700">{deviceStatus('temporarily_blocked').label}</div>
            <div className="mt-1 text-2xl font-bold text-rose-900">{metrics.blocked}</div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {(['all', 'pending_approval', 'active', 'temporarily_blocked'] as const).map((key) => (
            <button
              key={key}
              type="button"
              className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                filter === key
                  ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                  : 'border-stone-200 text-stone-700 hover:bg-stone-50'
              }`}
              onClick={() => setFilter(key)}
            >
              {key === 'all' ? 'All' : deviceStatus(key).label}
            </button>
          ))}
        </div>

        <div className="mt-4 rounded-xl border border-stone-200 bg-white">
          {loading ? (
            <div className="px-4 py-8 text-sm text-stone-600">Loading…</div>
          ) : filtered.length ? (
            <div className="divide-y divide-stone-100">
              {filtered.map((device) => (
                <div
                  key={device.id}
                  data-testid="device-row"
                  className="flex flex-wrap items-start justify-between gap-3 px-4 py-4"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Smartphone className="h-4 w-4 shrink-0 text-stone-500" />
                      <span className="font-semibold text-stone-900">{device.deviceName}</span>
                      <ShortCodeBadge code={device.shortCode} />
                      <span
                        className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${deviceStatusClass(device.status)}`}
                      >
                        {deviceStatus(device.status).label}
                      </span>
                      {device.activeShift ? (
                        <Link
                          to={`/shifts/${encodeURIComponent(device.activeShift.id)}`}
                          className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-800 hover:underline"
                        >
                          In use (shift active)
                        </Link>
                      ) : null}
                      {device.approvalRequest && device.status !== 'pending_approval' ? (
                        <span className="rounded-full border border-amber-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                          Waiting for approval
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 text-xs text-stone-600">{deviceDescription(device)}</div>
                    {device.lastCashier ? (
                      <div className="mt-1 text-xs text-stone-700">
                        Last used by {cashierRefLabel(device.lastCashier)}
                      </div>
                    ) : null}
                    <div className="mt-1 text-xs text-stone-500">
                      Registered {formatInTimezone(device.firstRegisteredAt || device.createdAt, timezone)}
                      {device.lastActiveAt ? ` · Last active ${formatInTimezone(device.lastActiveAt, timezone)}` : ''}
                      {device.approvedAt && device.status === 'active'
                        ? ` · Approved ${formatInTimezone(device.approvedAt, timezone)}${
                            device.approvalMethod === 'pairing_code' ? ' with a pairing code' : ''
                          }`
                        : ''}
                      {device.lastIp ? ` · IP ${device.lastIp}` : ''}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    {(device.status === 'pending_approval' && !device.approvalRequest) ||
                    device.status === 'temporarily_blocked' ? (
                      <button
                        type="button"
                        disabled={actionId === device.id}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                        onClick={() => openApprove(device)}
                      >
                        <CheckCircle className="h-4 w-4" />
                        {device.status === 'temporarily_blocked' ? 'Unblock' : 'Approve'}
                      </button>
                    ) : null}
                    {device.status === 'active' ? (
                      <button
                        type="button"
                        disabled={actionId === device.id}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                        onClick={() => setBlockTarget(device)}
                      >
                        <ShieldOff className="h-4 w-4" />
                        Block
                      </button>
                    ) : null}
                    <button
                      type="button"
                      disabled={actionId === device.id}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-60"
                      onClick={() => openRename(device)}
                    >
                      <Pencil className="h-4 w-4" />
                      Rename
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <PortalEmptyState
              icon={<Smartphone className="mx-auto h-12 w-12 text-stone-400" strokeWidth={1.5} />}
              title="No devices found"
              description={
                filter === 'all'
                  ? 'Devices appear here after a Cashier signs in on them, or when a counter uses a pairing code.'
                  : 'No devices match this filter.'
              }
            />
          )}
        </div>
      </div>

      {approveTarget ? (
        <ModalShell
          titleId="approve-device-title"
          title={approveTarget.status === 'temporarily_blocked' ? 'Unblock device' : 'Approve device'}
        >
          <div className="mt-3 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <div className="font-mono text-3xl font-bold tracking-[0.2em] text-amber-950">{approveTarget.shortCode}</div>
            <p className="text-sm text-amber-900">
              Check that code {approveTarget.shortCode} is shown on the counter screen.
            </p>
          </div>
          <div className="mt-3 text-sm text-stone-700">
            <div>{deviceDescription(approveTarget)}</div>
            {approveTarget.approvalRequest ? (
              <div className="mt-1 text-stone-600">
                {cashierRefLabel(approveTarget.approvalRequest.cashier)} · shift{' '}
                {formatInTimezone(approveTarget.approvalRequest.shift.scheduledStartAt, timezone)}
              </div>
            ) : null}
          </div>
          <div className="mt-4">
            <label htmlFor="approve-device-name" className="text-sm font-medium text-stone-700">
              Device name
            </label>
            <input
              id="approve-device-name"
              value={approveName}
              onChange={(e) => setApproveName(e.target.value)}
              maxLength={DEVICE_NAME_MAX}
              placeholder="e.g. Counter 1"
              className={inputClass}
            />
            <p className={`mt-1 text-xs ${approveNameValid ? 'text-stone-500' : 'text-rose-700'}`}>
              {approveNameValid
                ? 'Give it a name staff recognize, e.g. "Counter 1".'
                : `Enter a name (1–${DEVICE_NAME_MAX} characters).`}
            </p>
          </div>
          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              className={secondaryButtonClass}
              onClick={() => setApproveTarget(null)}
              disabled={actionId === approveTarget.id}
            >
              Cancel
            </button>
            <button
              type="button"
              className={primaryButtonClass}
              disabled={actionId === approveTarget.id || !approveNameValid}
              onClick={() => void onConfirmApprove(approveTarget)}
            >
              {actionId === approveTarget.id
                ? 'Saving…'
                : approveTarget.status === 'temporarily_blocked'
                  ? 'Unblock'
                  : 'Approve'}
            </button>
          </div>
        </ModalShell>
      ) : null}

      {rejectTarget ? (
        <ModalShell titleId="reject-device-title" title="Reject device">
          <p className="mt-1 text-sm text-stone-600">
            Reject {rejectTarget.deviceName} ({rejectTarget.shortCode})? The device is blocked and the Cashier cannot
            start a shift on it. You can unblock it later.
          </p>
          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              className={secondaryButtonClass}
              onClick={() => setRejectTarget(null)}
              disabled={actionId === rejectTarget.id}
            >
              Cancel
            </button>
            <button
              type="button"
              className={dangerButtonClass}
              disabled={actionId === rejectTarget.id}
              onClick={() => void onConfirmReject(rejectTarget)}
            >
              {actionId === rejectTarget.id ? 'Rejecting…' : 'Reject'}
            </button>
          </div>
        </ModalShell>
      ) : null}

      {blockTarget ? (
        <ModalShell titleId="block-device-title" title="Block device">
          <p className="mt-1 text-sm text-stone-600">
            Block {blockTarget.deviceName}? Active sessions on this device will be signed out.
          </p>
          {blockTarget.activeShift ? (
            <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              A shift is running on this device. It cannot take deposits or redeems until you move it to another
              device.
            </p>
          ) : null}
          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              className={secondaryButtonClass}
              onClick={() => setBlockTarget(null)}
              disabled={actionId === blockTarget.id}
            >
              Cancel
            </button>
            <button
              type="button"
              className={dangerButtonClass}
              disabled={actionId === blockTarget.id}
              onClick={() => void onConfirmBlock(blockTarget)}
            >
              {actionId === blockTarget.id ? 'Blocking…' : 'Block'}
            </button>
          </div>
        </ModalShell>
      ) : null}

      {renameTarget ? (
        <ModalShell titleId="rename-device-title" title="Rename device">
          <div className="mt-4">
            <label htmlFor="rename-device-name" className="text-sm font-medium text-stone-700">
              Device name
            </label>
            <input
              id="rename-device-name"
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              maxLength={DEVICE_NAME_MAX}
              className={inputClass}
            />
            {!renameNameValid ? (
              <p className="mt-1 text-xs text-rose-700">Enter a name (1–{DEVICE_NAME_MAX} characters).</p>
            ) : null}
          </div>
          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              className={secondaryButtonClass}
              onClick={() => setRenameTarget(null)}
              disabled={actionId === renameTarget.id}
            >
              Cancel
            </button>
            <button
              type="button"
              className={primaryButtonClass}
              disabled={actionId === renameTarget.id || !renameNameValid}
              onClick={() => void onConfirmRename(renameTarget)}
            >
              {actionId === renameTarget.id ? 'Saving…' : 'Save'}
            </button>
          </div>
        </ModalShell>
      ) : null}

      {pairing ? (
        <ModalShell titleId="pairing-code-title" title="Add device with pairing code">
          {pairing.result ? (
            <>
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-5 text-center">
                <div
                  className="font-mono text-4xl font-bold tracking-[0.15em] text-emerald-950"
                  data-testid="pairing-code"
                >
                  {pairing.result.code}
                </div>
                {pairing.result.name ? (
                  <div className="mt-1 text-sm text-emerald-900">Device name: {pairing.result.name}</div>
                ) : null}
                <div className="mt-2 text-sm text-stone-700">
                  <PairingCountdown expiresAt={pairing.result.expiresAt} />
                </div>
              </div>
              <p className="mt-3 text-sm text-stone-600">
                Type this code on the counter device (Start shift → Have a pairing code?). It works once and expires in
                10 minutes.
              </p>
              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  type="button"
                  className={secondaryButtonClass}
                  onClick={() => setPairing({ name: pairing.name, creating: false, result: null })}
                >
                  New code
                </button>
                <button type="button" className={primaryButtonClass} onClick={closePairing}>
                  Done
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="mt-1 text-sm text-stone-600">
                Create a one-time code and type it on the counter device to approve it without matching codes.
              </p>
              <div className="mt-4">
                <label htmlFor="pairing-device-name" className="text-sm font-medium text-stone-700">
                  Device name (optional)
                </label>
                <input
                  id="pairing-device-name"
                  value={pairing.name}
                  onChange={(e) => setPairing({ ...pairing, name: e.target.value })}
                  maxLength={DEVICE_NAME_MAX}
                  disabled={pairing.creating}
                  placeholder="e.g. Counter 2"
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-stone-500">The device gets this name when the code is used.</p>
              </div>
              <div className="mt-5 flex items-center justify-end gap-2">
                <button type="button" className={secondaryButtonClass} onClick={closePairing} disabled={pairing.creating}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={primaryButtonClass}
                  disabled={pairing.creating}
                  onClick={() => void onCreatePairingCode(pairing)}
                >
                  {pairing.creating ? 'Creating…' : 'Create code'}
                </button>
              </div>
            </>
          )}
        </ModalShell>
      ) : null}
    </AppLayout>
  );
}
